import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatDate";
import {
  RotateCcw,
  Download,
  Loader,
  Trash2,
  StopCircle,
  ArrowLeft,
} from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";
import { apiCall } from "../api";
import "./WorkOrderDetail.css";
import ExtractionComparison from "./ExtractionComparison";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.js";

const WorkOrderDetail = () => {
  const { workOrderId } = useParams();
  const navigate = useNavigate();
  const [workOrder, setWorkOrder] = useState(null);
  const [files, setFiles] = useState([]);
  const [extractions, setExtractions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [callbackLogs, setCallbackLogs] = useState([]);
  const [accessLogs, setAccessLogs] = useState([]);
  const [changeLogs, setChangeLogs] = useState([]);
  const [reports, setReports] = useState([]);
  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [extracting, setExtracting] = useState(false);

  // Initialize progress from sessionStorage to persist across navigation
  const [estimatedProgress, setEstimatedProgress] = useState(() => {
    const saved = sessionStorage.getItem(`wo-progress-${workOrderId}`);
    return saved ? parseInt(saved, 10) : 0;
  });

  const [pdfDocument, setPdfDocument] = useState(null);
  const [selectedFileForPreview, setSelectedFileForPreview] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewModalFile, setReviewModalFile] = useState(null);
  const [modalDataTab, setModalDataTab] = useState("meta");
  const [modalHighlightedField, setModalHighlightedField] = useState(null);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [newPropertyName, setNewPropertyName] = useState("");
  const [propertyNameError, setPropertyNameError] = useState("");
  const [editingCell, setEditingCell] = useState(null); // {groupKey, fieldKey}
  const [editingValue, setEditingValue] = useState("");
  const pdfContainerRef = React.useRef(null);

  // Track if this is the first status check
  const isFirstStatusCheckRef = React.useRef(true);

  // Helper to get extraction for the currently selected review file
  const getExtractionForFile = useCallback(
    (fileId) => {
      if (!fileId || !extractions || extractions.length === 0) return null;
      return extractions.find((ex) => ex.fileId === fileId) || null;
    },
    [extractions],
  );

  // Get extraction for currently open review modal file
  const currentFileExtraction = reviewModalFile
    ? getExtractionForFile(reviewModalFile._id)
    : extractions.length > 0
      ? extractions[0]
      : null;

  useEffect(() => {
    if (!workOrderId) return;
    fetchWorkOrderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderId]);

  const fetchWorkOrderData = async () => {
    if (!workOrderId) return;
    try {
      setLoading(true);
      const [
        woRes,
        filesRes,
        extraRes,
        logsRes,
        cbRes,
        accessRes,
        changesRes,
        reportsRes,
      ] = await Promise.all([
        apiCall(`/api/work-orders/${workOrderId}`).catch(() => null),
        apiCall(`/api/work-orders/${workOrderId}/files`).catch(() => null),
        apiCall(`/api/work-orders/${workOrderId}/extractions`).catch(
          () => null,
        ),
        apiCall(`/api/work-orders/${workOrderId}/logs`).catch(() => null),
        apiCall(`/api/work-orders/${workOrderId}/callback-logs`).catch(
          () => null,
        ),
        apiCall(`/api/work-orders/${workOrderId}/review-access-logs`).catch(
          () => null,
        ),
        apiCall(`/api/work-orders/${workOrderId}/change-logs`).catch(
          () => null,
        ),
        apiCall(`/api/work-orders/${workOrderId}/reports`).catch(() => null),
      ]);

      if (!woRes) throw new Error("Work order not found");

      setWorkOrder(woRes);
      setFiles(Array.isArray(filesRes) ? filesRes : []);
      setExtractions(Array.isArray(extraRes) ? extraRes : []);
      setLogs(Array.isArray(logsRes) ? logsRes : logsRes?.logs || []);
      setCallbackLogs(Array.isArray(cbRes) ? cbRes : cbRes?.callbackLogs || []);
      setAccessLogs(
        Array.isArray(accessRes) ? accessRes : accessRes?.logs || [],
      );
      setChangeLogs(
        Array.isArray(changesRes) ? changesRes : changesRes?.changes || [],
      );
      setReports(
        Array.isArray(reportsRes) ? reportsRes : reportsRes?.reports || [],
      );

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const uploadedFiles = event.target.files;
    if (!uploadedFiles) return;

    for (let file of uploadedFiles) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await fetch(`/api/work-orders/${workOrderId}/files`, {
          method: "POST",
          body: formData,
        });

        if (response.ok) {
          const newFile = await response.json();
          setFiles([...files, newFile]);
        }
      } catch (err) {
        setError(`Failed to upload ${file.name}: ${err.message}`);
      }
    }

    // Auto-trigger extraction after file upload
    setTimeout(() => handleExtract(), 1000);
  };

  const handleExtract = async () => {
    setExtracting(true);
    setError("");
    console.log(`[Extract] Starting extraction for work order ${workOrderId}`);
    try {
      const response = await fetch(`/api/work-orders/${workOrderId}/extract`, {
        method: "POST",
      });

      console.log(`[Extract] Response status: ${response.status}`);

      if (response.ok || response.status === 202) {
        // Both 200 (completed) and 202 (accepted) mean extraction started
        console.log(`[Extract] Extraction started successfully`);
        try {
          const data = await response.json();
          console.log(`[Extract] Response data:`, data);
          if (data.extractionResults) {
            setExtractions(data.extractionResults);
          }
        } catch (e) {
          // Response might be empty for 202
          console.log(`[Extract] No JSON response body (expected for 202)`);
        }
        setWorkOrder({ ...workOrder, status: "processing" });
        // Keep extracting true - it will auto-refresh until completion
      } else {
        const errorData = await response
          .json()
          .catch(() => ({ error: response.statusText }));
        console.error(
          `[Extract] Failed with status ${response.status}:`,
          errorData,
        );
        setError(
          `Extraction failed: ${errorData.error || response.statusText}`,
        );
        setExtracting(false);
      }
    } catch (err) {
      console.error(`[Extract] Error:`, err);
      setError(`Extraction error: ${err.message}`);
      setExtracting(false);
    }
  };

  const handleRetryExtraction = async () => {
    // Retry extraction for stuck work orders (created/failed state)
    if (!window.confirm("Retry extraction for this work order?")) {
      return;
    }

    setError("");
    setEstimatedProgress(0);
    sessionStorage.removeItem(`wo-progress-${workOrderId}`);

    await handleExtract();
  };

  // Sync extracting state with processing status only
  // Note: "created" state allows retry, so don't set extracting=true for it
  useEffect(() => {
    if (workOrder?.status === "processing") {
      setExtracting(true);
      // Only reset to 0 on FIRST check (when status changes to processing from something else)
      if (isFirstStatusCheckRef.current && estimatedProgress === 0) {
        isFirstStatusCheckRef.current = false;
        setEstimatedProgress(0);
      } else if (isFirstStatusCheckRef.current) {
        // Don't reset if we already have progress saved from previous navigation
        isFirstStatusCheckRef.current = false;
      }
    } else if (workOrder?.status === "created") {
      // Allow retry for stuck created state
      setExtracting(false);
      // Don't reset progress - user might want to see what failed before
    } else if (
      workOrder?.status &&
      workOrder.status !== "processing" &&
      workOrder.status !== "created"
    ) {
      setExtracting(false);
      if (workOrder?.status === "completed" || workOrder?.status === "failed") {
        setEstimatedProgress(100);
        // Clear saved progress when completed/failed
        sessionStorage.removeItem(`wo-progress-${workOrderId}`);
      }
    }
  }, [workOrder?.status, workOrderId]);

  // Animate estimated progress while extracting
  useEffect(() => {
    if (!extracting) return;

    const interval = setInterval(() => {
      setEstimatedProgress((prev) => {
        // 0-50%: increase 1% per second (0.5% per 500ms)
        if (prev < 50) return prev + 0.5;
        // 50-85%: slower
        if (prev < 85) return prev + 0.15;
        // 85-95%: very slow
        return Math.min(prev + 0.05, 95);
      });
    }, 500);

    return () => clearInterval(interval);
  }, [extracting]);

  // Persist progress to sessionStorage to survive navigation
  useEffect(() => {
    if (extracting && estimatedProgress > 0) {
      sessionStorage.setItem(
        `wo-progress-${workOrderId}`,
        String(estimatedProgress),
      );
    }
  }, [estimatedProgress, extracting, workOrderId]);

  // Reset first status check when workOrderId changes
  useEffect(() => {
    isFirstStatusCheckRef.current = true;
  }, [workOrderId]);

  // SSE Streaming for real-time work order updates
  useEffect(() => {
    if (!workOrderId) {
      console.log("No workOrderId, skipping SSE setup");
      return;
    }

    console.log(`[SSE] Setting up stream for work order: ${workOrderId}`);
    let eventSource = null;
    let isMounted = true;

    const setupSSE = () => {
      try {
        eventSource = new EventSource(`/api/work-orders/${workOrderId}/stream`);
        console.log(`[SSE] Connection established for ${workOrderId}`);

        eventSource.addEventListener("message", (event) => {
          if (!isMounted) return;
          try {
            const data = JSON.parse(event.data);
            console.log(`[SSE] Received event:`, data.type);

            if (data.type === "status_update") {
              console.log(
                `[SSE] Status update: ${data.status}, Progress: ${data.fileProgress}%`,
              );
              setWorkOrder((prev) => ({
                ...prev,
                status: data.status,
                fileProgress: data.fileProgress,
              }));
            } else if (data.type === "log_update") {
              console.log(`[SSE] New log: ${data.log.eventName}`);
              setLogs((prev) => [...prev, data.log]);
            } else if (data.type === "complete") {
              console.log(`[SSE] Extraction complete, closing stream`);
              if (eventSource) eventSource.close();
              fetchWorkOrderData();
            } else if (data.error) {
              console.error("[SSE] Error:", data.error);
            }
          } catch (err) {
            console.error("[SSE] Failed to parse data:", err);
          }
        });

        eventSource.onerror = (error) => {
          if (!isMounted) return;
          console.error("[SSE] Connection error:", error.type);
          eventSource.close();
        };
      } catch (err) {
        console.error("[SSE] Failed to setup:", err);
      }
    };

    setupSSE();

    return () => {
      console.log(`[SSE] Cleaning up stream for ${workOrderId}`);
      isMounted = false;
      if (eventSource) {
        eventSource.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderId]);

  const handleRerunExtraction = async () => {
    const apiUrl = process.env.REACT_APP_API_URL || "";

    // Allow retry for pending and processing states
    if (!["pending", "processing"].includes(workOrder.status)) {
      setError(
        "Can only retry extraction for pending or processing work orders",
      );
      return;
    }

    try {
      setExtracting(true);
      setError("");

      const response = await fetch(
        `${apiUrl}/api/work-orders/${workOrderId}/retry`,
        { method: "POST" },
      );

      if (!response.ok) {
        throw new Error("Failed to retry extraction");
      }

      const data = await response.json();
      setEstimatedProgress(0);
      alert(`✅ Retry started - Attempt #${data.retryCount}`);

      // Refresh work order data
      await fetchWorkOrderData();
    } catch (err) {
      setError(`Retry failed: ${err.message}`);
    } finally {
      setExtracting(false);
    }
  };

  const handleCancelExtraction = async () => {
    const apiUrl = process.env.REACT_APP_API_URL || "";

    if (!["pending", "processing"].includes(workOrder.status)) {
      setError("Can only cancel pending or processing work orders");
      return;
    }

    if (
      !window.confirm(
        "Are you sure you want to cancel this work order extraction?",
      )
    ) {
      return;
    }

    try {
      setExtracting(true);
      setError("");

      const response = await fetch(
        `${apiUrl}/api/work-orders/${workOrderId}/cancel`,
        { method: "POST" },
      );

      if (!response.ok) {
        throw new Error("Failed to cancel extraction");
      }

      alert("✅ Extraction cancelled");
      await fetchWorkOrderData();
    } catch (err) {
      setError(`Cancel failed: ${err.message}`);
    } finally {
      setExtracting(false);
    }
  };

  const handleDeleteWorkOrder = async () => {
    const apiUrl = process.env.REACT_APP_API_URL || "";

    if (
      !window.confirm(
        "Are you sure you want to delete this entire work order? This cannot be undone.",
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await fetch(`${apiUrl}/api/work-orders/${workOrderId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete work order");
      }

      alert("✅ Work order deleted successfully");
      window.location.href = "/work-orders";
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadPDF = async (fileId) => {
    try {
      setPdfLoading(true);
      setError("");
      const response = await fetch(
        `/api/work-orders/${workOrderId}/files/${fileId}/download`,
      );
      if (!response.ok) throw new Error("Failed to load PDF");

      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      setPdfDocument(pdf);
      setSelectedFileForPreview(fileId);
    } catch (err) {
      setError(`Failed to load PDF: ${err.message}`);
      setPdfDocument(null);
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    if (pdfDocument && pdfContainerRef.current) {
      renderAllPages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDocument, modalHighlightedField, currentFileExtraction]);

  // No longer needed - SSE handles real-time updates
  // useEffect for polling removed, replaced with SSE streaming

  const renderAllPages = useCallback(async () => {
    console.log(
      "📄 renderAllPages called. pdfDocument:",
      !!pdfDocument,
      "modalHighlightedField:",
      modalHighlightedField,
    );
    if (!pdfDocument || !pdfContainerRef.current) return;

    const container = pdfContainerRef.current;
    container.innerHTML = "";

    // Get bboxData from the current file's extraction
    const bboxData = currentFileExtraction?.bboxData || {};
    console.log(
      "📦 bboxData available:",
      Object.keys(bboxData).length,
      "keys:",
      Object.keys(bboxData),
    );

    for (let pageNum = 1; pageNum <= pdfDocument.numPages; pageNum++) {
      try {
        const page = await pdfDocument.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.2 });

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.className = "pdf-page";

        await page.render({
          canvasContext: context,
          viewport: viewport,
        }).promise;

        // Draw bounding box highlight if field is selected and has bbox data on this page
        if (modalHighlightedField && bboxData[modalHighlightedField]) {
          const bbox = bboxData[modalHighlightedField];
          console.log(
            `Field: ${modalHighlightedField}, bbox:`,
            bbox,
            `page:${bbox.page}, current:${pageNum}`,
          );

          if ((bbox.page || 1) === pageNum) {
            // Convert normalized coordinates (0-1 range) to canvas pixels
            // bbox.wmin/wmax are normalized width (0-1), bbox.hmin/hmax are normalized height (0-1)
            const x = bbox.wmin * viewport.width;
            const y = bbox.hmin * viewport.height;
            const w = (bbox.wmax - bbox.wmin) * viewport.width;
            const h = (bbox.hmax - bbox.hmin) * viewport.height;

            console.log(
              `Drawing normalized bbox at: x=${x.toFixed(1)}, y=${y.toFixed(1)}, w=${w.toFixed(1)}, h=${h.toFixed(1)}`,
            );

            // Draw semi-transparent orange fill
            context.fillStyle = "rgba(255, 165, 0, 0.3)";
            context.fillRect(x, y, w, h);

            // Draw thick orange border
            context.strokeStyle = "#ff6600";
            context.lineWidth = 5;
            context.strokeRect(x, y, w, h);

            // Draw border again with offset for glow effect
            context.strokeStyle = "rgba(255, 102, 0, 0.5)";
            context.lineWidth = 8;
            context.strokeRect(x - 2, y - 2, w + 4, h + 4);
          }
        }

        container.appendChild(canvas);
      } catch (err) {
        console.error(`Error rendering page ${pageNum}:`, err);
      }
    }
  }, [pdfDocument, modalHighlightedField, extractions]);

  const downloadFile = async (fileId, filename) => {
    try {
      const response = await fetch(
        `/api/work-orders/${workOrderId}/files/${fileId}/download`,
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
      }
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  };

  const exportToExcel = (data, filename) => {
    try {
      const jsonData = typeof data === "string" ? JSON.parse(data) : data;
      const csv = convertJsonToCsv(jsonData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `${filename}.csv`);
      link.click();
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    }
  };

  const exportAllExtractionsToExcel = () => {
    try {
      if (!extractions || extractions.length === 0) {
        setError("No extractions available to export");
        return;
      }

      // Download each extraction as a separate CSV file
      extractions.forEach((ext, idx) => {
        const file = files.find((f) => f._id === ext.fileId);
        const filename = file?.filename || `extraction_${idx + 1}`;
        const data = ext.extractedData || ext.data || {};

        // Flatten nested objects
        const flatData = {};
        const flatten = (obj, prefix = "") => {
          for (const [key, value] of Object.entries(obj)) {
            const newKey = prefix ? `${prefix}.${key}` : key;
            if (
              typeof value === "object" &&
              value !== null &&
              !Array.isArray(value)
            ) {
              flatten(value, newKey);
            } else {
              flatData[newKey] = value;
            }
          }
        };
        flatten(data);

        // Build CSV
        const headers = Object.keys(flatData);
        const values = headers.map((h) => {
          const val = flatData[h];
          const str = String(
            val === undefined || val === null ? "" : val,
          ).replace(/"/g, '""');
          return `"${str}"`;
        });
        const csv = [headers.join(","), values.join(",")].join("\n");

        // Remove .pdf extension and create clean filename
        const cleanName = filename.replace(/\.pdf$/i, "");
        const downloadName = `${cleanName}.csv`;

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", downloadName);

        // Small delay between downloads to prevent browser blocking
        setTimeout(() => {
          link.click();
          URL.revokeObjectURL(url);
        }, idx * 300);
      });
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    }
  };

  const convertJsonToCsv = (data) => {
    if (Array.isArray(data) && data.length > 0) {
      const headers = Object.keys(data[0]);
      const rows = data.map((obj) =>
        headers.map((header) => JSON.stringify(obj[header] || "")).join(","),
      );
      return [headers.join(","), ...rows].join("\n");
    } else if (typeof data === "object") {
      const headers = Object.keys(data);
      const values = headers
        .map((key) => JSON.stringify(data[key] || ""))
        .join(",");
      return [headers.join(","), values].join("\n");
    }
    return "";
  };

  const downloadJSON = (data, filename) => {
    try {
      const jsonStr =
        typeof data === "string" ? data : JSON.stringify(data, null, 2);
      const blob = new Blob([jsonStr], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${filename}.json`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Failed to download JSON: ${err.message}`);
    }
  };

  const downloadAllExtractionsJSON = () => {
    try {
      console.log("[Download] extractions array:", extractions);
      console.log("[Download] extractions.length:", extractions?.length);
      console.log("[Download] files array:", files);
      console.log("[Download] files.length:", files?.length);

      if (!extractions || extractions.length === 0) {
        setError("No extractions available to download");
        return;
      }

      // Download each extraction as a separate JSON file
      extractions.forEach((ext, idx) => {
        const file = files.find((f) => f._id === ext.fileId);
        const filename = file?.filename || `extraction_${idx + 1}`;
        const formType = file?.formType || ext.formType || "Unknown";

        // Remove .pdf extension and create clean filename
        const cleanName = filename.replace(/\.pdf$/i, "");
        const downloadName = `${cleanName}.json`;

        const data = ext.extractedData || ext.data || {};
        const jsonStr = JSON.stringify(data, null, 2);

        const blob = new Blob([jsonStr], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = downloadName;

        // Small delay between downloads to prevent browser blocking
        setTimeout(() => {
          link.click();
          window.URL.revokeObjectURL(url);
        }, idx * 300);
      });
    } catch (err) {
      console.error("[Download] Error:", err);
      setError(`Failed to download JSON: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="work-order-detail loading">
        <div className="spinner"></div>
        <p>Loading work order...</p>
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="work-order-detail">
        <div className="error-message">{error || "Work order not found"}</div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      created: "#3b82f6",
      processing: "#f59e0b", // Orange
      completed: "#10b981", // Green (Processed)
      pending: "#f59e0b", // Orange
      failed: "#ef4444", // Red
      cancelled: "#ef4444", // Red
    };
    return colors[status] || "#6b7280";
  };

  return (
    <div className="work-order-detail">
      {/* Header */}
      <div className="detail-header">
        <button
          onClick={() => navigate("/work-orders")}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "#3b82f6",
            fontSize: "14px",
            fontWeight: "500",
            padding: "8px 0",
            marginBottom: "16px",
          }}
          title="Back to Work Orders"
        >
          <ArrowLeft size={18} />
          Back to Work Orders
        </button>
        <div className="header-left">
          <h1>Work Order</h1>
          <div className="work-order-id">ID: {workOrder._id}</div>
        </div>
        <div
          className="header-right"
          style={{ display: "flex", gap: "1rem", alignItems: "center" }}
        >
          {extractions.length > 0 && (
            <button
              onClick={() => {
                const token = localStorage.getItem("token");
                window.open(
                  `/api/work-orders/${workOrder._id}/report/download?token=${token}`,
                  "_blank",
                );
              }}
              style={{
                padding: "0.6rem 1.2rem",
                background: "#f3f4f6",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: 500,
                fontSize: "0.95rem",
                color: "#667eea",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <Download size={18} />
              Excel Report
            </button>
          )}

          <span
            className="status-badge"
            style={{
              backgroundColor: getStatusColor(workOrder.status),
              color: "white",
            }}
          >
            {workOrder.status === "completed"
              ? "Processed"
              : workOrder.status.toUpperCase()}
          </span>
          {["pending", "processing"].includes(workOrder.status) && (
            <button
              className="btn btn-primary"
              onClick={handleRerunExtraction}
              disabled={extracting}
              title={
                workOrder.status === "processing"
                  ? "Retry stuck extraction"
                  : "Re-run extraction"
              }
            >
              <RotateCcw size={16} />
              {extracting
                ? "Retrying..."
                : workOrder.status === "processing"
                  ? "Retry Extraction"
                  : "Re-run Extraction"}
            </button>
          )}
          {["pending", "processing"].includes(workOrder.status) && (
            <button
              className="btn btn-warning"
              onClick={handleCancelExtraction}
              title="Cancel extraction immediately"
              style={{
                background: "#f97316",
                color: "white",
                border: "none",
              }}
            >
              <StopCircle size={16} />
              Cancel
            </button>
          )}
          <button
            className="btn btn-danger"
            onClick={handleDeleteWorkOrder}
            title="Delete this work order permanently"
            style={{
              background: "#ef4444",
              color: "white",
              border: "none",
            }}
          >
            <Trash2 size={16} />
            Delete
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <strong>Error:</strong> {error}
          <button onClick={() => setError("")}>✕</button>
        </div>
      )}

      {/* Extraction In Progress Banner */}
      {extracting && (
        <div
          style={{
            background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
            color: "white",
            padding: "1rem 1.5rem",
            borderRadius: "8px",
            marginBottom: "1.5rem",
            animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              marginBottom: "0.75rem",
            }}
          >
            <Loader
              size={24}
              style={{ animation: "spin 1s linear infinite", flexShrink: 0 }}
            />
            <div>
              <div style={{ fontWeight: 700, fontSize: "1rem" }}>
                {workOrder?.status === "processing"
                  ? "🔄 Extraction in Progress"
                  : "⏳ Ready for Extraction"}
              </div>
              <div
                style={{
                  fontSize: "0.9rem",
                  opacity: 0.9,
                  marginTop: "0.25rem",
                }}
              >
                {workOrder?.status === "processing"
                  ? `${logs.length} pipeline events captured • Updating every 500ms`
                  : "Upload a file to start extraction"}
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          {files.length > 0 && (
            <div style={{ marginTop: "1rem" }}>
              <div
                style={{
                  fontSize: "0.85rem",
                  marginBottom: "0.5rem",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <span>File Processing Progress</span>
                <span style={{ fontWeight: 600 }}>
                  {Math.round(workOrder?.fileProgress || estimatedProgress)}%
                  {(workOrder?.fileProgress === 0 ||
                    workOrder?.fileProgress === undefined) &&
                  extracting
                    ? " (estimated)"
                    : ""}{" "}
                  complete
                </span>
              </div>
              <div
                style={{
                  width: "100%",
                  height: "8px",
                  background: "rgba(255,255,255,0.3)",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${Math.max(workOrder?.fileProgress || estimatedProgress, 5)}%`,
                    height: "100%",
                    background: "white",
                    transition: "width 0.3s ease",
                    borderRadius: "4px",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
      `}</style>

      {/* 8 Tabs - Professional Layout */}
      <div className="tabs-container">
        <div className="tabs">
          {[
            { id: "general", label: "General", icon: "📋" },
            { id: "files", label: "Files", count: files.length },
            { id: "logs", label: "Logs" },
            { id: "comparison", label: "Provider Comparison" },
            {
              id: "callbackLogs",
              label: "Callback Logs",
              count: callbackLogs.length,
            },
            {
              id: "accessLogs",
              label: "Review Access Logs",
              count: accessLogs.length,
            },
            {
              id: "changeLogs",
              label: "Change Logs",
              count: changeLogs.length,
            },
            { id: "reports", label: "Reports", count: reports.length },
          ].map((tab) => (
            <button
              key={tab.id}
              className={`tab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
              title={tab.label}
            >
              <span className="tab-label">{tab.label}</span>
              {tab.count !== undefined && tab.count > 0 && (
                <span className="tab-badge">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* General Tab */}
          {activeTab === "general" && (
            <div className="tab-pane">
              {/* Retry Extraction Button for Stuck Work Orders (only if stuck for 30+ seconds) */}
              {(() => {
                if (!workOrder) return null;

                // Check if work order has been stuck in created state for more than 30 seconds
                let isStuckInCreated = false;
                if (workOrder?.status === "created" && workOrder?.createdAt) {
                  try {
                    const createdAtValue = workOrder.createdAt;

                    // Parse the timestamp - handle ISO strings, Date objects, and timestamps
                    let createdTime;
                    if (typeof createdAtValue === "string") {
                      // ISO string from backend (e.g., "2026-08-04T12:34:56.789012")
                      createdTime = new Date(createdAtValue).getTime();
                    } else if (createdAtValue instanceof Date) {
                      createdTime = createdAtValue.getTime();
                    } else if (typeof createdAtValue === "number") {
                      // If it's already a timestamp
                      createdTime = createdAtValue;
                    } else {
                      console.warn(
                        "[WorkOrder] Unexpected createdAt type:",
                        typeof createdAtValue,
                        createdAtValue,
                      );
                      isStuckInCreated = false;
                      createdTime = null;
                    }

                    if (createdTime && !isNaN(createdTime) && createdTime > 0) {
                      const nowTime = Date.now();
                      const timeDiffMs = nowTime - createdTime;

                      // ONLY show stuck alert if work order has been in created state for > 30 seconds
                      // AND the created timestamp is reasonable (not in future, not extremely old)
                      const isReasonableTimestamp =
                        createdTime < nowTime && timeDiffMs < 86400000; // less than 24 hours old
                      isStuckInCreated =
                        isReasonableTimestamp && timeDiffMs > 30000;

                      console.log(
                        `[WorkOrder Check] status: ${workOrder.status}, createdAt: ${createdAtValue}, diff: ${(timeDiffMs / 1000).toFixed(1)}s, isReasonable: ${isReasonableTimestamp}, isStuck: ${isStuckInCreated}`,
                      );
                    } else {
                      console.error(
                        "[WorkOrder] Invalid timestamp:",
                        createdTime,
                        "from:",
                        createdAtValue,
                      );
                    }
                  } catch (err) {
                    console.error(
                      "[WorkOrder] Error checking stuck status:",
                      err,
                      "createdAt:",
                      workOrder.createdAt,
                    );
                    isStuckInCreated = false;
                  }
                }

                // Show alert for failed status or truly stuck created status
                const shouldShowAlert =
                  workOrder?.status === "failed" || isStuckInCreated;

                if (!shouldShowAlert) return null;

                return (
                  <div
                    style={{
                      marginBottom: "1.5rem",
                      padding: "1rem",
                      backgroundColor: "#fef3c7",
                      border: "1px solid #fbbf24",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <h4 style={{ margin: "0 0 0.25rem 0", color: "#92400e" }}>
                        ⚠️ Work Order Stuck
                      </h4>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.875rem",
                          color: "#b45309",
                        }}
                      >
                        {workOrder?.status === "failed"
                          ? "Extraction failed. You can retry with the existing work order and files."
                          : "This work order is stuck in created state for 30+ seconds. Try retrying the extraction."}
                      </p>
                    </div>
                    <button
                      onClick={handleRetryExtraction}
                      disabled={extracting}
                      style={{
                        padding: "0.75rem 1.5rem",
                        backgroundColor: "#f59e0b",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: extracting ? "not-allowed" : "pointer",
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        marginLeft: "1rem",
                        opacity: extracting ? 0.6 : 1,
                      }}
                    >
                      {extracting ? "⏳ Retrying..." : "⚠️ Retry Extraction"}
                    </button>
                  </div>
                );
              })()}

              <div className="details-wrapper">
                <div className="details-container">
                  <div className="details-header">
                    <h3>Basic Details</h3>
                  </div>
                  <div className="details-grid">
                    <div className="detail-item">
                      <label>Work Order ID</label>
                      <code>{workOrder._id}</code>
                    </div>
                    <div className="detail-item">
                      <label>Trace ID</label>
                      <code>{workOrder.traceId || "—"}</code>
                    </div>
                    <div className="detail-item">
                      <label>Customer Name</label>
                      <span>{workOrder.customerName}</span>
                    </div>
                    <div className="detail-item">
                      <label>Associated Party</label>
                      <span>{workOrder.associatedParty || "—"}</span>
                    </div>
                    <div className="detail-item">
                      <label>Report Type</label>
                      <span>{workOrder.reportType}</span>
                    </div>
                    <div className="detail-item">
                      <label>Mode</label>
                      <span>{workOrder.mode}</span>
                    </div>
                    <div className="detail-item">
                      <label>Status</label>
                      <span
                        style={{
                          color: getStatusColor(workOrder.status),
                          fontWeight: 1000,
                        }}
                      >
                        {workOrder.status === "completed"
                          ? "PROCESSED"
                          : workOrder.status.toUpperCase()}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>File Progress</label>
                      <span>{workOrder.fileProgress || 0}%</span>
                    </div>
                    <div className="detail-item full-width">
                      <label>Created At</label>
                      <span>{formatDate(workOrder.createdAt)}</span>
                    </div>
                    <div className="detail-item full-width">
                      <label>Updated At</label>
                      <span>{formatDate(workOrder.updatedAt)}</span>
                    </div>
                    <div className="detail-item full-width">
                      <label>Created By</label>
                      <span>{workOrder.createdBy}</span>
                    </div>
                    {workOrder.api_log && (
                      <>
                        <div className="detail-item full-width">
                          <label>Method</label>
                          <span>{workOrder.api_log.method || "—"}</span>
                        </div>
                        <div className="detail-item full-width">
                          <label>Endpoint</label>
                          <span>{workOrder.api_log.endpoint || "—"}</span>
                        </div>
                        <div className="detail-item full-width">
                          <label>IP</label>
                          <span>{workOrder.api_log.ip_address || "—"}</span>
                        </div>
                        <div className="detail-item full-width">
                          <label>Status</label>
                          <span>
                            {workOrder.api_log.status || "—"}
                            {workOrder.api_log.status_code ? ` (${workOrder.api_log.status_code})` : ""}
                          </span>
                        </div>
                        <div className="detail-item full-width">
                          <label>Request payload</label>
                          <span>
                            {workOrder.api_log.request_params &&
                            Object.keys(workOrder.api_log.request_params).length > 0
                              ? JSON.stringify(workOrder.api_log.request_params)
                              : "—"}
                          </span>
                        </div>
                        <div className="detail-item full-width">
                          <label>Response Time</label>
                          <span>
                            {workOrder.api_log.processing_time_seconds != null
                              ? `${parseFloat(workOrder.api_log.processing_time_seconds).toFixed(3)}s`
                              : "—"}
                          </span>
                        </div>
                        <div className="detail-item full-width">
                          <label>API Created By</label>
                          <span>{workOrder.api_log.created_by || "—"}</span>
                        </div>
                        <div className="detail-item full-width">
                          <label>Timestamp</label>
                          <span>{formatDate(workOrder.api_log.timestamp)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Reports Section */}
                {extractions.length > 0 && (
                  <div style={{ flex: 1, paddingLeft: "2rem" }}>
                    <h3
                      style={{
                        margin: "0 0 1rem 0",
                        fontSize: "0.95rem",
                        fontWeight: 600,
                        color: "#374151",
                        textTransform: "capitalize",
                      }}
                    >
                      Reports
                    </h3>
                    <div
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: "6px",
                        overflow: "hidden",
                      }}
                    >
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                        }}
                      >
                        <tbody>
                          <tr
                            style={{
                              borderBottom: "1px solid #e5e7eb",
                              background: "#fafafa",
                            }}
                          >
                            <td
                              style={{
                                padding: "1rem",
                                color: "#6b7280",
                                fontSize: "0.95rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                                flex: 1,
                              }}
                            >
                              <span style={{ fontSize: "1.2rem" }}>📎</span>
                              <span>
                                Excel ({extractions.length} file
                                {extractions.length > 1 ? "s" : ""})
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "1rem",
                                textAlign: "right",
                              }}
                            >
                              <button
                                onClick={exportAllExtractionsToExcel}
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: "#667eea",
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                  fontSize: "0.9rem",
                                  fontWeight: 600,
                                  padding: 0,
                                }}
                              >
                                Download
                              </button>
                            </td>
                          </tr>
                          <tr style={{ background: "#ffffff" }}>
                            <td
                              style={{
                                padding: "1rem",
                                color: "#6b7280",
                                fontSize: "0.95rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                                flex: 1,
                              }}
                            >
                              <span style={{ fontSize: "1.2rem" }}>📎</span>
                              <span>
                                JSON ({extractions.length} file
                                {extractions.length > 1 ? "s" : ""})
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "1rem",
                                textAlign: "right",
                              }}
                            >
                              <button
                                onClick={downloadAllExtractionsJSON}
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: "#667eea",
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                  fontSize: "0.9rem",
                                  fontWeight: 600,
                                  padding: 0,
                                }}
                              >
                                Download
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Files Tab */}
          {activeTab === "files" && (
            <div className="tab-pane">
              <div className="section">
                <div className="section-header">
                  <h3>📁 Files ({files.length})</h3>
                  <div style={{ display: "flex", gap: "0.75rem" }}>
                    {/* Re-Analyze button (Retry button moved to General tab) */}
                    <button
                      className="btn btn-primary"
                      onClick={handleExtract}
                      disabled={extracting || files.length === 0}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      {extracting ? "⏳ Re-Analyze" : "🔄 Re-Analyze"}
                    </button>

                    <label className="btn btn-primary">
                      + Add files
                      <input
                        type="file"
                        multiple
                        accept=".pdf"
                        onChange={handleFileUpload}
                        style={{ display: "none" }}
                      />
                    </label>
                  </div>
                </div>

                {files.length === 0 ? (
                  <div className="empty-state">
                    <p>No files uploaded yet</p>
                    <small>Upload PDF files to begin extraction</small>
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <thead>
                        <tr
                          style={{
                            borderBottom: "2px solid #e5e7eb",
                            background: "#f9fafb",
                          }}
                        >
                          <th
                            style={{
                              padding: "0.75rem",
                              textAlign: "left",
                              fontWeight: 600,
                              color: "#6b7280",
                            }}
                          >
                            Name
                          </th>
                          <th
                            style={{
                              padding: "0.75rem",
                              textAlign: "left",
                              fontWeight: 600,
                              color: "#6b7280",
                            }}
                          >
                            Form Type
                          </th>
                          <th
                            style={{
                              padding: "0.75rem",
                              textAlign: "left",
                              fontWeight: 600,
                              color: "#6b7280",
                            }}
                          >
                            Range
                          </th>
                          <th
                            style={{
                              padding: "0.75rem",
                              textAlign: "center",
                              fontWeight: 600,
                              color: "#6b7280",
                            }}
                          >
                            Num pages
                          </th>
                          <th
                            style={{
                              padding: "0.75rem",
                              textAlign: "center",
                              fontWeight: 600,
                              color: "#6b7280",
                            }}
                          >
                            Is scanned
                          </th>
                          <th
                            style={{
                              padding: "0.75rem",
                              textAlign: "center",
                              fontWeight: 600,
                              color: "#6b7280",
                            }}
                          >
                            Is file valid
                          </th>
                          <th
                            style={{
                              padding: "0.75rem",
                              textAlign: "left",
                              fontWeight: 600,
                              color: "#6b7280",
                            }}
                          >
                            Status
                          </th>
                          <th
                            style={{
                              padding: "0.75rem",
                              textAlign: "left",
                              fontWeight: 600,
                              color: "#6b7280",
                            }}
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {files.map((file, idx) => (
                          <tr
                            key={file._id}
                            style={{ borderBottom: "1px solid #e5e7eb" }}
                          >
                            <td
                              style={{ padding: "0.75rem", color: "#1f2937" }}
                            >
                              📄 {file.filename}
                            </td>
                            <td
                              style={{ padding: "0.75rem", color: "#6b7280" }}
                            >
                              {file.formType || "PDF"}
                            </td>
                            <td
                              style={{ padding: "0.75rem", color: "#6b7280" }}
                            >
                              —
                            </td>
                            <td
                              style={{
                                padding: "0.75rem",
                                textAlign: "center",
                                color: "#6b7280",
                              }}
                            >
                              {file.numPages || 0}
                            </td>
                            <td
                              style={{
                                padding: "0.75rem",
                                textAlign: "center",
                              }}
                            >
                              <span
                                style={{
                                  color: file.isScanned ? "#10b981" : "#ef4444",
                                }}
                              >
                                {file.isScanned ? "✓" : "✕"}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "0.75rem",
                                textAlign: "center",
                              }}
                            >
                              <span
                                style={{
                                  color: file.isValid ? "#10b981" : "#ef4444",
                                }}
                              >
                                {file.isValid !== false ? "✓" : "✕"}
                              </span>
                            </td>
                            <td style={{ padding: "0.75rem" }}>
                              <span
                                style={{
                                  padding: "0.25rem 0.75rem",
                                  borderRadius: "4px",
                                  fontSize: "0.85rem",
                                  fontWeight: 600,
                                  background:
                                    file.status === "processed"
                                      ? "#d1fae5"
                                      : "#dbeafe",
                                  color:
                                    file.status === "processed"
                                      ? "#059669"
                                      : "#0369a1",
                                }}
                              >
                                {file.status === "processed"
                                  ? "✓ Processed"
                                  : "Pending"}
                              </span>
                            </td>
                            <td
                              style={{ padding: "0.75rem", fontSize: "0.9rem" }}
                            >
                              {extractions &&
                              extractions.some(
                                (ex) => ex.fileId === file._id,
                              ) ? (
                                <div style={{ display: "flex", gap: "0.5rem" }}>
                                  <button
                                    style={{
                                      padding: "0.4rem 0.8rem",
                                      background: "#667eea",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                      fontSize: "0.85rem",
                                      fontWeight: 600,
                                      transition: "all 0.2s",
                                    }}
                                    onMouseOver={(e) =>
                                      (e.target.style.background = "#5568d3")
                                    }
                                    onMouseOut={(e) =>
                                      (e.target.style.background = "#667eea")
                                    }
                                    onClick={() => {
                                      setReviewModalFile(file);
                                      setReviewModalOpen(true);
                                      loadPDF(file._id);
                                    }}
                                  >
                                    Review
                                  </button>
                                  <button
                                    style={{
                                      padding: "0.4rem 0.8rem",
                                      background: "#f3f4f6",
                                      color: "#374151",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                      fontSize: "0.85rem",
                                      fontWeight: 600,
                                      transition: "all 0.2s",
                                    }}
                                    onMouseOver={(e) => {
                                      e.target.style.background = "#e5e7eb";
                                      e.target.style.borderColor = "#9ca3af";
                                    }}
                                    onMouseOut={(e) => {
                                      e.target.style.background = "#f3f4f6";
                                      e.target.style.borderColor = "#d1d5db";
                                    }}
                                    onClick={() =>
                                      downloadFile(file._id, file.filename)
                                    }
                                  >
                                    View File
                                  </button>
                                </div>
                              ) : (
                                <span
                                  style={{
                                    color: "#9ca3af",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  Awaiting extraction
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Logs Tab */}
          {activeTab === "logs" && (
            <div className="tab-pane">
              <div className="section">
                <h3>Event Logs</h3>
                {extracting && (
                  <div
                    style={{
                      padding: "1rem",
                      background: "#f0f9ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: "6px",
                      marginBottom: "1.5rem",
                    }}
                  >
                    <div style={{ color: "#0369a1", fontWeight: 600 }}>
                      ⏳ Extraction in progress - {logs.length} events captured
                    </div>
                    <div
                      style={{
                        color: "#0369a1",
                        fontSize: "0.9rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      🔄 Logs updating in real-time (every 500ms)
                    </div>
                  </div>
                )}
                {logs.length === 0 ? (
                  <div className="empty-state">
                    <p>
                      {extracting
                        ? "⏳ Waiting for extraction logs..."
                        : "No logs available"}
                    </p>
                    {extracting && (
                      <p
                        style={{
                          fontSize: "0.9rem",
                          color: "#6b7280",
                          marginTop: "0.5rem",
                        }}
                      >
                        Logs will appear here as extraction progresses
                      </p>
                    )}
                  </div>
                ) : (
                  <div style={{ position: "relative", paddingLeft: "2rem" }}>
                    {logs.map((log, idx) => {
                      const isError =
                        log.level === "ERROR" ||
                        log.logType?.includes("ERROR") ||
                        log.logType?.includes("FAILED") ||
                        log.logType?.includes("EXCEPTION");
                      const logName = log.message || log.logType || "Event";
                      const logType = log.logType || "";
                      const timestamp = formatDate(
                        log.createdAt || log.timestamp,
                      );

                      return (
                        <div
                          key={idx}
                          style={{
                            position: "relative",
                            marginBottom: "1.5rem",
                            paddingBottom: "1rem",
                          }}
                        >
                          {/* Timeline dot */}
                          <div
                            style={{
                              position: "absolute",
                              left: "-2rem",
                              top: "0.25rem",
                              width: "1.5rem",
                              height: "1.5rem",
                              borderRadius: "50%",
                              background: isError ? "#ef4444" : "#10b981",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontWeight: 700,
                              fontSize: "0.75rem",
                            }}
                          >
                            {isError ? "✕" : "✓"}
                          </div>

                          {/* Timeline line (connect dots) */}
                          {idx < logs.length - 1 && (
                            <div
                              style={{
                                position: "absolute",
                                left: "-1.75rem",
                                top: "1.5rem",
                                width: "2px",
                                height: "1rem",
                                background: "#e5e7eb",
                              }}
                            />
                          )}

                          {/* Log content */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: isError ? "#dc2626" : "#1f2937",
                                  fontSize: "0.95rem",
                                }}
                              >
                                {logName}
                                {logType && (
                                  <span
                                    style={{
                                      fontWeight: 400,
                                      color: "#6b7280",
                                      fontSize: "0.8rem",
                                      marginLeft: 6,
                                    }}
                                  >
                                    ({logType})
                                  </span>
                                )}
                              </div>
                            </div>
                            <div
                              style={{ color: "#9ca3af", fontSize: "0.85rem" }}
                            >
                              {timestamp}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Callback Logs Tab */}
          {activeTab === "callbackLogs" && (
            <div className="tab-pane">
              <div className="section">
                <h3>🔄 Callback Logs</h3>
                {callbackLogs.length === 0 ? (
                  <div className="empty-state">
                    <p>No callback logs available</p>
                  </div>
                ) : (
                  <div className="logs-list">
                    {callbackLogs.map((log, idx) => (
                      <div key={idx} className="log-entry">
                        <div className="log-timestamp">
                          {new Date(
                            log.createdAt || log.timestamp,
                          ).toLocaleString()}
                        </div>
                        <div className="log-message">{log.message}</div>
                        <div className="log-status">Status: {log.status}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Review Access Logs Tab */}
          {activeTab === "accessLogs" && (
            <div className="tab-pane">
              <div className="section">
                <h3>👁️ Review Access Logs</h3>
                {accessLogs.length === 0 ? (
                  <div className="empty-state">
                    <p>No access logs available</p>
                  </div>
                ) : (
                  <div className="logs-list">
                    {accessLogs.map((log, idx) => (
                      <div key={idx} className="log-entry">
                        <div className="log-timestamp">
                          {new Date(
                            log.createdAt || log.timestamp,
                          ).toLocaleString()}
                        </div>
                        <div className="log-message">
                          {log.action} by {log.user}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Change Logs Tab */}
          {activeTab === "changeLogs" && (
            <div className="tab-pane">
              <div className="section">
                <h3>📊 Change Logs</h3>
                {changeLogs.length === 0 ? (
                  <div className="empty-state">
                    <p>No changes recorded</p>
                  </div>
                ) : (
                  <div className="logs-list">
                    {changeLogs.map((log, idx) => (
                      <div key={idx} className="log-entry">
                        <div className="log-timestamp">
                          {new Date(
                            log.createdAt || log.timestamp,
                          ).toLocaleString()}
                        </div>
                        <div className="log-message">
                          {log.field}: {log.oldValue} → {log.newValue}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reports Tab */}
          {activeTab === "reports" && (
            <div className="tab-pane">
              <div className="section">
                <h3>📄 Work Order Reports</h3>
                {!workOrder ? (
                  <div className="empty-state">
                    <p>Loading...</p>
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <thead>
                        <tr
                          style={{
                            backgroundColor: "#f5f5f5",
                            borderBottom: "2px solid #ddd",
                          }}
                        >
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              fontWeight: "600",
                            }}
                          >
                            Uploaded PDF
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "center",
                              fontWeight: "600",
                            }}
                          >
                            Generated JSON
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "center",
                              fontWeight: "600",
                            }}
                          >
                            Downloadable Excel
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              fontWeight: "600",
                            }}
                          >
                            Created At
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom: "1px solid #eee" }}>
                          <td style={{ padding: "12px" }}>
                            {files && files.length > 0 ? (
                              <span
                                onClick={() => {
                                  setReviewModalFile(files[0]);
                                  setReviewModalOpen(true);
                                  loadPDF(files[0]._id);
                                }}
                                style={{
                                  color: "#667eea",
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                }}
                              >
                                View PDF
                              </span>
                            ) : (
                              <span style={{ color: "#999" }}>-</span>
                            )}
                          </td>
                          <td style={{ padding: "12px", textAlign: "center" }}>
                            {extractions && extractions.length > 0 ? (
                              <span
                                onClick={downloadAllExtractionsJSON}
                                style={{
                                  color: "#667eea",
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                }}
                              >
                                Download JSON ({extractions.length} file
                                {extractions.length > 1 ? "s" : ""})
                              </span>
                            ) : (
                              <span style={{ color: "#999" }}>-</span>
                            )}
                          </td>
                          <td style={{ padding: "12px", textAlign: "center" }}>
                            {extractions && extractions.length > 0 ? (
                              <span
                                onClick={exportAllExtractionsToExcel}
                                style={{
                                  color: "#667eea",
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                }}
                              >
                                Download Excel ({extractions.length} file
                                {extractions.length > 1 ? "s" : ""})
                              </span>
                            ) : (
                              <span style={{ color: "#999" }}>-</span>
                            )}
                          </td>
                          <td style={{ padding: "12px" }}>
                            {workOrder.createdAt
                              ? formatDate(workOrder.createdAt)
                              : "N/A"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* GLIB Comparison Tab */}
          {activeTab === "comparison" && (
            <div className="tab-pane">
              <ExtractionComparison workOrderId={workOrderId} />
            </div>
          )}
        </div>

        {/* Review Modal */}
        {reviewModalOpen && reviewModalFile && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
            onClick={() => setReviewModalOpen(false)}
          >
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                width: "95%",
                height: "90vh",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                overflow: "hidden",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div
                style={{
                  padding: "1.5rem",
                  borderBottom: "1px solid #e5e7eb",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h2 style={{ margin: 0 }}>
                  📄 {reviewModalFile.filename} - Review
                </h2>
                <button
                  onClick={() => setReviewModalOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "1.5rem",
                    cursor: "pointer",
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Modal Body */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "2rem",
                  padding: "2rem",
                  overflow: "hidden",
                  flex: 1,
                }}
              >
                {/* PDF Preview */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <h3 style={{ margin: "0 0 1rem 0", fontSize: "1.1rem" }}>
                    📄 Document
                  </h3>
                  {pdfLoading ? (
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f9fafb",
                        borderRadius: "8px",
                      }}
                    >
                      ⏳ Loading PDF...
                    </div>
                  ) : pdfDocument && selectedFileForPreview ? (
                    <div
                      style={{
                        flex: 1,
                        overflow: "hidden",
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <div
                        ref={pdfContainerRef}
                        style={{
                          height: "100%",
                          overflowY: "auto",
                          background: "#f9fafb",
                          padding: "1rem",
                        }}
                      ></div>
                    </div>
                  ) : (
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f9fafb",
                        borderRadius: "8px",
                      }}
                    >
                      📄 PDF not loaded
                    </div>
                  )}
                </div>

                {/* Extracted Data */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "1rem",
                    }}
                  >
                    <h3 style={{ margin: 0, fontSize: "1.1rem" }}>
                      📊 Extracted Data
                    </h3>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button
                        onClick={() => setModalDataTab("meta")}
                        style={{
                          padding: "0.5rem 1rem",
                          background:
                            modalDataTab === "meta" ? "#667eea" : "white",
                          color: modalDataTab === "meta" ? "white" : "#333",
                          border: `1px solid ${modalDataTab === "meta" ? "#667eea" : "#ddd"}`,
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "0.85rem",
                          fontWeight: 500,
                        }}
                      >
                        Meta
                      </button>
                      <button
                        onClick={() => setModalDataTab("json")}
                        style={{
                          padding: "0.5rem 1rem",
                          background:
                            modalDataTab === "json" ? "#667eea" : "white",
                          color: modalDataTab === "json" ? "white" : "#333",
                          border: `1px solid ${modalDataTab === "json" ? "#667eea" : "#ddd"}`,
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "0.85rem",
                          fontWeight: 500,
                        }}
                      >
                        JSON
                      </button>
                      <button
                        onClick={() => setShowAddPropertyModal(true)}
                        style={{
                          padding: "0.5rem 1rem",
                          background: "rgba(102, 126, 234, 0.1)",
                          color: "#667eea",
                          border: "1px solid #667eea",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "0.85rem",
                          fontWeight: 500,
                          marginLeft: "auto",
                        }}
                      >
                        + Add Property
                      </button>
                    </div>
                  </div>
                  {currentFileExtraction ? (
                    <div
                      style={{
                        flex: 1,
                        overflowY: "auto",
                        background: "#f9fafb",
                        padding: "1rem",
                        borderRadius: "8px",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      {modalDataTab === "json" ? (
                        <pre
                          style={{
                            margin: 0,
                            fontSize: "0.85rem",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {JSON.stringify(
                            currentFileExtraction?.extractedData ||
                              currentFileExtraction ||
                              {},
                            null,
                            2,
                          )}
                        </pre>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "1.5rem",
                          }}
                        >
                          {Object.entries(
                            currentFileExtraction?.extractedData || {},
                          ).map(([groupKey, groupValue]) => {
                            // Check if value is a nested object (group) or a flat value
                            const isGroup =
                              typeof groupValue === "object" &&
                              groupValue !== null &&
                              !Array.isArray(groupValue);
                            const entries = isGroup
                              ? Object.entries(groupValue)
                              : [[groupKey, groupValue]];

                            return (
                              <div
                                key={groupKey}
                                style={{
                                  borderRadius: "8px",
                                  border: "1px solid #e5e7eb",
                                  overflow: "hidden",
                                  background: "white",
                                }}
                              >
                                {/* Section Header */}
                                <div
                                  style={{
                                    padding: "0.75rem 1rem",
                                    background: "#f3f4f6",
                                    borderBottom: "1px solid #e5e7eb",
                                    fontWeight: 600,
                                    fontSize: "0.9rem",
                                    color: "#1f2937",
                                  }}
                                >
                                  {isGroup
                                    ? `${groupKey.replace(/_/g, " ").toUpperCase()}`
                                    : groupKey}
                                </div>

                                {/* Table */}
                                <table
                                  style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  <thead>
                                    <tr
                                      style={{
                                        background: "#f9fafb",
                                        borderBottom: "1px solid #e5e7eb",
                                      }}
                                    >
                                      <th
                                        style={{
                                          padding: "0.75rem 1rem",
                                          textAlign: "left",
                                          fontWeight: 600,
                                          color: "#374151",
                                          borderRight: "1px solid #e5e7eb",
                                        }}
                                      >
                                        Key
                                      </th>
                                      <th
                                        style={{
                                          padding: "0.75rem 1rem",
                                          textAlign: "left",
                                          fontWeight: 600,
                                          color: "#374151",
                                        }}
                                      >
                                        Value
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {entries.map(([key, value], idx) => {
                                      const isEditing =
                                        editingCell?.groupKey === groupKey &&
                                        editingCell?.fieldKey === key;
                                      const isHighlighted =
                                        modalHighlightedField === key;

                                      return (
                                        <tr
                                          key={`${groupKey}-${key}`}
                                          style={{
                                            background: isHighlighted
                                              ? "#dbeafe"
                                              : idx % 2 === 0
                                                ? "white"
                                                : "#f9fafb",
                                            transition: "background 0.2s",
                                            borderBottom: "1px solid #e5e7eb",
                                          }}
                                        >
                                          <td
                                            style={{
                                              padding: "0.75rem 1rem",
                                              fontWeight: 500,
                                              color: "#1f2937",
                                              borderRight: "1px solid #e5e7eb",
                                              maxWidth: "150px",
                                              wordBreak: "break-word",
                                            }}
                                          >
                                            {key}
                                          </td>
                                          <td
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              console.log(
                                                "🔍 VALUE CLICKED:",
                                                key,
                                                "in group:",
                                                groupKey,
                                              );
                                              // Get bbox data from extractions
                                              const bboxData =
                                                extractions.length > 0
                                                  ? extractions[0].bboxData ||
                                                    {}
                                                  : {};
                                              // For nested fields, construct compound key like "groupKey.fieldKey"
                                              const compoundKey = `${groupKey}.${key}`;
                                              // Try compound key first, then individual field, then group
                                              const bboxLookupKey = bboxData[
                                                compoundKey
                                              ]
                                                ? compoundKey
                                                : bboxData[key]
                                                  ? key
                                                  : bboxData[groupKey]
                                                    ? groupKey
                                                    : null;
                                              console.log(
                                                "📍 bbox lookup key:",
                                                bboxLookupKey,
                                                "(tried:",
                                                compoundKey,
                                                key,
                                                groupKey,
                                                ")",
                                              );
                                              setModalHighlightedField(
                                                bboxLookupKey || key,
                                              ); // Use bbox key if available
                                              setEditingCell({
                                                groupKey,
                                                fieldKey: key,
                                              });
                                              setEditingValue(
                                                typeof value === "object"
                                                  ? JSON.stringify(
                                                      value,
                                                      null,
                                                      2,
                                                    )
                                                  : String(value),
                                              );
                                            }}
                                            style={{
                                              padding: "0.75rem 1rem",
                                              color: "#374151",
                                              fontFamily: "Monaco, monospace",
                                              fontSize: "0.8rem",
                                              maxWidth: "300px",
                                              wordBreak: "break-word",
                                              whiteSpace: "pre-wrap",
                                              cursor: "pointer",
                                              background: isEditing
                                                ? "#fff3cd"
                                                : "transparent",
                                              transition: "background 0.2s",
                                            }}
                                          >
                                            {isEditing ? (
                                              <div
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                                style={{
                                                  display: "flex",
                                                  gap: "0.5rem",
                                                }}
                                              >
                                                <textarea
                                                  autoFocus
                                                  value={editingValue}
                                                  onChange={(e) =>
                                                    setEditingValue(
                                                      e.target.value,
                                                    )
                                                  }
                                                  style={{
                                                    flex: 1,
                                                    padding: "0.5rem",
                                                    border: "1px solid #667eea",
                                                    borderRadius: "4px",
                                                    fontFamily:
                                                      "Monaco, monospace",
                                                    fontSize: "0.8rem",
                                                    minHeight: "60px",
                                                  }}
                                                />
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: "0.25rem",
                                                  }}
                                                >
                                                  <button
                                                    onClick={async (e) => {
                                                      e.stopPropagation();
                                                      try {
                                                        const extraction =
                                                          extractions[0];
                                                        const fieldName = `${groupKey}.${key}`;

                                                        console.log(
                                                          "💾 SAVING FIELD:",
                                                          fieldName,
                                                          "Value:",
                                                          editingValue,
                                                        );

                                                        const response =
                                                          await fetch(
                                                            `/api/work-orders/${workOrderId}/extractions/${extraction._id}/fields/${fieldName}`,
                                                            {
                                                              method: "PUT",
                                                              headers: {
                                                                "Content-Type":
                                                                  "application/json",
                                                              },
                                                              body: JSON.stringify(
                                                                {
                                                                  value:
                                                                    editingValue,
                                                                  validationStatus:
                                                                    "VALID",
                                                                },
                                                              ),
                                                            },
                                                          );

                                                        console.log(
                                                          "✅ Response status:",
                                                          response.status,
                                                        );
                                                        const result =
                                                          await response.json();
                                                        console.log(
                                                          "✅ Response data:",
                                                          result,
                                                        );

                                                        if (response.ok) {
                                                          // Update local state only - don't reload entire extraction
                                                          setExtractions(
                                                            (prev) => {
                                                              const updated = [
                                                                ...prev,
                                                              ];
                                                              if (updated[0]) {
                                                                if (
                                                                  !updated[0]
                                                                    .extractedData[
                                                                    groupKey
                                                                  ]
                                                                ) {
                                                                  updated[0].extractedData[
                                                                    groupKey
                                                                  ] = {};
                                                                }
                                                                updated[0].extractedData[
                                                                  groupKey
                                                                ][key] =
                                                                  editingValue;
                                                              }
                                                              return updated;
                                                            },
                                                          );
                                                          alert(
                                                            "✅ Field saved!",
                                                          );
                                                          setEditingCell(null);
                                                        } else {
                                                          alert(
                                                            "❌ Failed to save: " +
                                                              (result.error ||
                                                                response.statusText),
                                                          );
                                                        }
                                                      } catch (err) {
                                                        console.error(
                                                          "❌ Save error:",
                                                          err,
                                                        );
                                                        alert(
                                                          "❌ Error saving field: " +
                                                            err.message,
                                                        );
                                                      }
                                                    }}
                                                    style={{
                                                      padding: "0.25rem 0.5rem",
                                                      background: "#10b981",
                                                      color: "white",
                                                      border: "none",
                                                      borderRadius: "3px",
                                                      cursor: "pointer",
                                                      fontSize: "0.7rem",
                                                      fontWeight: 600,
                                                    }}
                                                  >
                                                    Save
                                                  </button>
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setEditingCell(null);
                                                    }}
                                                    style={{
                                                      padding: "0.25rem 0.5rem",
                                                      background: "#ef4444",
                                                      color: "white",
                                                      border: "none",
                                                      borderRadius: "3px",
                                                      cursor: "pointer",
                                                      fontSize: "0.7rem",
                                                      fontWeight: 600,
                                                    }}
                                                  >
                                                    Cancel
                                                  </button>
                                                </div>
                                              </div>
                                            ) : typeof value === "object" ? (
                                              JSON.stringify(value, null, 2)
                                            ) : (
                                              String(value)
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f9fafb",
                        borderRadius: "8px",
                        color: "#999",
                      }}
                    >
                      No extraction data available. Click Re-Analyze to extract
                      data.
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div
                style={{
                  padding: "1.5rem",
                  borderTop: "1px solid #e5e7eb",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "1rem",
                }}
              >
                <button
                  onClick={() => setReviewModalOpen(false)}
                  style={{
                    padding: "0.5rem 1.5rem",
                    background: "#f3f4f6",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Close
                </button>
                <button
                  style={{
                    padding: "0.5rem 1.5rem",
                    background: "#10b981",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  ✓ Approve
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Property Modal */}
        {showAddPropertyModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10000,
            }}
            onClick={() => setShowAddPropertyModal(false)}
          >
            <div
              style={{
                background: "white",
                borderRadius: "8px",
                boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
                maxWidth: "500px",
                width: "90%",
                maxHeight: "90vh",
                overflowY: "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "1.5rem",
                  borderBottom: "1px solid #e5e7eb",
                }}
              >
                <h2 style={{ margin: 0, fontSize: "1.25rem" }}>
                  Add New Property
                </h2>
                <button
                  onClick={() => setShowAddPropertyModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "1.5rem",
                    cursor: "pointer",
                    color: "#999",
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{ padding: "1.5rem" }}>
                <div style={{ marginBottom: "1rem" }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      marginBottom: "0.5rem",
                      color: "#333",
                    }}
                  >
                    Property Name *
                  </label>
                  <input
                    type="text"
                    value={newPropertyName}
                    onChange={(e) => {
                      setNewPropertyName(e.target.value);
                      setPropertyNameError("");
                    }}
                    placeholder="e.g., customerName, dateReceived"
                    autoFocus
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      border: `1px solid ${propertyNameError ? "#f87171" : "#ddd"}`,
                      borderRadius: "6px",
                      fontSize: "0.9rem",
                      boxSizing: "border-box",
                    }}
                  />
                  {propertyNameError && (
                    <span
                      style={{
                        fontSize: "0.8rem",
                        color: "#991b1b",
                        marginTop: "0.25rem",
                        display: "block",
                      }}
                    >
                      {propertyNameError}
                    </span>
                  )}
                </div>
              </div>

              <div
                style={{
                  padding: "0 1.5rem 1.5rem",
                  display: "flex",
                  gap: "1rem",
                  justifyContent: "flex-end",
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <button
                  onClick={() => setShowAddPropertyModal(false)}
                  style={{
                    padding: "0.75rem 1.5rem",
                    background: "#f3f4f6",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!newPropertyName.trim()) {
                      setPropertyNameError("Property name cannot be empty");
                      return;
                    }
                    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(newPropertyName)) {
                      setPropertyNameError(
                        "Property name must start with letter or underscore, contain only alphanumeric and underscore",
                      );
                      return;
                    }
                    setShowAddPropertyModal(false);
                    setNewPropertyName("");
                    setPropertyNameError("");
                  }}
                  style={{
                    padding: "0.75rem 1.5rem",
                    background: "#667eea",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                  }}
                >
                  Create Property
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkOrderDetail;
